import type { Dictionary, ExportFormat, ImportEntry, ImportResult } from "@/types";
import type { AxiosError } from "axios";

import { defineStore } from "pinia";

import { useAPI } from "@/composable/useAPI";

import { useSnackbarStore } from "@/stores/useSnackbarStore";
import { useVGuildStore } from "@/stores/useVGuildStore";

const getGuildId = () => useVGuildStore().vGuild.guild_id;

const handleError = (_axiosError: AxiosError) => {
  useSnackbarStore().addDefaultGetError();
};

export const useDictionaryStore = defineStore("dictionaries", {
  state: () => {
    return {
      dictionaries: [] as Dictionary[],
    };
  },

  actions: {
    async fetchFromVGuild() {
      const guildId = getGuildId();
      if (guildId === "0") return;

      const response = await useAPI()
        .get(`/api/v_guilds/${guildId}/dictionaries`)
        .catch(handleError);
      if (!response) return;

      this.dictionaries = response.data;
    },
    async create(dictionary: Pick<Dictionary, "before" | "after">) {
      const guildId = getGuildId();
      if (guildId === "0") return;

      useAPI()
        .post(`/api/v_guilds/${guildId}/dictionaries`, dictionary)
        .then(({ data }) => {
          this.dictionaries.push(data);
          useSnackbarStore().addTopRight({ color: "success", text: "更新しました" });
        })
        .catch((error) => {
          useSnackbarStore().addError(error);
        });
    },
    async update(dictionary: Partial<Dictionary>) {
      const guildId = getGuildId();
      if (guildId === "0") return;

      const response = await useAPI()
        .patch(`/api/v_guilds/${guildId}/dictionaries/${dictionary.id}`, {
          before: dictionary.before,
          after: dictionary.after,
        })
        .catch(handleError);
      if (!response) return;

      const index = this.dictionaries.findIndex(({ id }) => id === dictionary.id);
      if (index < 0) return;

      this.dictionaries.splice(index, 1, response.data);
      useSnackbarStore().addTopRight({ color: "success", text: "更新しました" });
    },
    async delete(ids: (string | bigint)[]) {
      const guildId = getGuildId();
      if (guildId === "0") return;

      const response = await useAPI()
        .delete(`/api/v_guilds/${guildId}/dictionaries`, { data: { ids } })
        .catch(handleError);
      if (!response) return false;

      this.dictionaries = this.dictionaries.filter(({ id }) => !ids.includes(id));
      useSnackbarStore().addTopRight({ color: "success", text: "削除しました" });
      return true;
    },
    async exportDictionary(format: ExportFormat) {
      const guildId = getGuildId();
      if (guildId === "0") return;

      const response = await useAPI({ responseType: "blob" })
        .get(`/api/v_guilds/${guildId}/dictionaries/export`, { params: { format } })
        .catch(handleError);
      if (!response) return;

      const url = URL.createObjectURL(response.data as Blob);
      const a = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"] as string | undefined;
      const filename =
        contentDisposition?.match(/filename="?([^"]+)"?/)?.[1] ?? `dictionary.${format}`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    },
    exportSelectedDictionaries(ids: (string | bigint)[], format: ExportFormat) {
      const selected = ids
        .map((id) => this.dictionaries.find((d) => d.id === id))
        .filter((d): d is Dictionary => d !== undefined);
      if (selected.length === 0) return;

      const escapeCsv = (s: string, delimiter: string) => {
        if (s.includes(delimiter) || s.includes('"') || s.includes("\n")) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      let content: string;
      let mimeType: string;
      if (format === "json") {
        content = JSON.stringify(
          selected.map(({ before, after }) => ({ before, after })),
          null,
          2,
        );
        mimeType = "application/json";
      } else {
        const delimiter = format === "tsv" ? "\t" : ",";
        const rows = [
          [escapeCsv("変換前", delimiter), escapeCsv("変換後", delimiter)].join(delimiter),
          ...selected.map(({ before, after }) =>
            [escapeCsv(before, delimiter), escapeCsv(after, delimiter)].join(delimiter),
          ),
        ];
        content = rows.join("\n");
        mimeType = format === "tsv" ? "text/tab-separated-values" : "text/csv";
      }

      const bom = format === "json" ? "" : "\uFEFF";
      const blob = new Blob([bom + content], { type: `${mimeType};charset=utf-8` });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dictionary_selected.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    },
    async fetchFromOtherGuild(guildId: string): Promise<Dictionary[] | undefined> {
      const response = await useAPI()
        .get(`/api/v_guilds/${guildId}/dictionaries`)
        .catch(handleError);
      if (!response) return;

      return response.data as Dictionary[];
    },
    async importDictionary(entries: ImportEntry[]): Promise<ImportResult | undefined> {
      const guildId = getGuildId();
      if (guildId === "0") return;

      const response = await useAPI()
        .post(`/api/v_guilds/${guildId}/dictionaries/import`, { dictionaries: entries })
        .catch(handleError);
      if (!response) return;

      await this.fetchFromVGuild();
      return response.data as ImportResult;
    },
  },
});
